import { useState } from 'react'
import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import {
  ArrowLeft,
  CalendarDays,
  CircleDollarSign,
  MailCheck,
  ShieldAlert,
  WalletCards,
} from 'lucide-react'
import { Button, ErrorState, MoneyValue, Skeleton, StatusBadge } from '@shared/design-system/ui'
import type { AdminEligibility, AdminUserDetail, ProductItem } from '../api/admin.api'
import { useAdminKycVerification, useAdminProducts } from '../api/admin.api'
import { AdminUserPerformance } from './AdminUserPerformance'
import { AdminCustomerSuccessPanel } from './AdminCustomerSuccessPanel'
import { AdminUserSalesPanel } from './AdminUserSalesPanel'
import { AdminKycSupportPanel } from './AdminKycSupportPanel'
import { TechnicalId } from './TechnicalId'
import { kycStatusLabels, receivingPresentation } from './kyc-support-labels'
import { onboardingBadgeLabels, operationLabels, statusTone } from './kyc-onboarding-labels'
import type { AdminOnboarding } from '../api/admin.api'
import {
  attention,
  attentionCopy,
  avatar,
  badgeRow,
  definition,
  definitionGrid,
  definitionLabel,
  definitionValue,
  emptyCopy,
  eyebrow,
  fullDefinition,
  helper,
  hero,
  heroIdentity,
  identityCopy,
  productCard,
  productIdentity,
  productList,
  productMeta,
  section,
  sectionDescription,
  sectionHeader,
  sectionHeading,
  sectionTitle,
  strongValue,
  summaryCard,
  summaryCopy,
  summaryGrid,
  summaryIcon,
  tab,
  tabPanel,
  tabs,
  backLink,
  pageContent,
  pageShell,
  userEmail,
  userName,
} from './AdminUserDetailScreen.css'

type UserDetailTab =
  | 'overview'
  | 'success'
  | 'profile'
  | 'products'
  | 'sales'
  | 'plan'
  | 'referrals'

type AdminUserDetailScreenProps = {
  userId: string
  user: AdminUserDetail | null
  loading: boolean
  error: boolean
  canReadProducts: boolean
  canWriteProducts: boolean
  canReadRevenue: boolean
  canReadSales: boolean
  receivingPlanPanel?: ReactNode
  referralsPanel?: ReactNode
  onRetry: () => void
  onEditProduct: (product: ProductItem) => void
}

export function AdminUserDetailScreen({
  userId,
  user,
  loading,
  error,
  canReadProducts,
  canWriteProducts,
  canReadRevenue,
  canReadSales,
  onRetry,
  receivingPlanPanel,
  referralsPanel,
  onEditProduct,
}: AdminUserDetailScreenProps) {
  const [activeTab, setActiveTab] = useState<UserDetailTab>('overview')
  const verification = useAdminKycVerification(user ? userId : null)

  return (
    <section className={pageShell} data-testid="usuario-detalhe-screen">
      <Link className={backLink} to="/usuarios">
        <ArrowLeft size={17} />
        Voltar para usuários
      </Link>
      {loading ? (
        <Skeleton height={420} />
      ) : error ? (
        <ErrorState
          title="Não foi possível carregar o usuário"
          description="Tente novamente. Nenhuma informação foi alterada."
          onRetry={onRetry}
        />
      ) : user ? (
        <div className={pageContent}>
          <UserHero
            user={user}
            eligibility={verification.data?.eligibility}
            onboarding={verification.data?.onboarding}
          />
          <UserTabs
            activeTab={activeTab}
            canReadProducts={canReadProducts}
            canReadSales={canReadSales}
            showReferrals={!!referralsPanel}
            onChange={setActiveTab}
          />

          {activeTab === 'overview' ? (
            <OverviewTab
              user={user}
              eligibility={verification.data?.eligibility}
              eligibilityUnavailable={verification.isPending || verification.isError}
              onboarding={
                verification.isPending || verification.isError
                  ? undefined
                  : verification.data?.onboarding
              }
              canReadRevenue={canReadRevenue}
              canReadProducts={canReadProducts}
            />
          ) : null}
          {activeTab === 'plan' ? receivingPlanPanel : null}
          {activeTab === 'referrals' ? referralsPanel : null}
          {activeTab === 'profile' ? <ProfileTab user={user} userId={userId} /> : null}
          {activeTab === 'success' ? (
            <div className={tabPanel} role="tabpanel">
              <AdminCustomerSuccessPanel
                key={userId}
                userId={userId}
                canReadRevenue={canReadRevenue}
              />
            </div>
          ) : null}
          {activeTab === 'products' && canReadProducts ? (
            <ProductsTab
              user={user}
              canWriteProducts={canWriteProducts}
              onEditProduct={onEditProduct}
            />
          ) : null}
          {activeTab === 'sales' && canReadSales ? <AdminUserSalesPanel userId={userId} /> : null}
        </div>
      ) : (
        <div className={emptyCopy}>Usuário não encontrado.</div>
      )}
    </section>
  )
}

function UserHero({
  user,
  eligibility,
  onboarding,
}: {
  user: AdminUserDetail
  eligibility: AdminEligibility | undefined
  onboarding: AdminOnboarding | undefined
}) {
  const badge = onboarding
    ? { label: onboardingBadgeLabels[onboarding.badge], tone: statusTone(onboarding.badge) }
    : eligibility
      ? receivingPresentation(eligibility)
      : null
  return (
    <div className={hero}>
      <div className={heroIdentity}>
        <span className={avatar}>{userInitials(user)}</span>
        <div className={identityCopy}>
          <h1 className={userName}>{profileName(user)}</h1>
          <span className={userEmail}>{user.email}</span>
          <TechnicalId label="ID" value={user.id} />
        </div>
      </div>
      {badge ? (
        <div className={badgeRow}>
          <StatusBadge label={badge.label} tone={badge.tone} />
          {onboarding?.blockedBy.sales === 'vendepay' ? (
            <StatusBadge label="Vendas bloqueadas pela VendePay" tone="closed" />
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function UserTabs({
  activeTab,
  canReadProducts,
  canReadSales,
  showReferrals,
  onChange,
}: {
  activeTab: UserDetailTab
  canReadProducts: boolean
  canReadSales: boolean
  showReferrals: boolean
  onChange: (tab: UserDetailTab) => void
}) {
  const items: Array<{ id: UserDetailTab; label: string }> = [
    { id: 'overview', label: 'Visão geral' },
    { id: 'success', label: 'Customer Success' },
    { id: 'profile', label: 'Cadastro e KYC' },
    ...(canReadProducts ? [{ id: 'products' as const, label: 'Produtos' }] : []),
    ...(canReadSales ? [{ id: 'sales' as const, label: 'Vendas' }] : []),
    { id: 'plan', label: 'Plano e taxas' },
    ...(showReferrals ? [{ id: 'referrals' as const, label: 'Indique e ganhe' }] : []),
  ]
  return (
    <div className={tabs} role="tablist" aria-label="Informações do usuário">
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          role="tab"
          aria-selected={activeTab === item.id}
          className={tab[activeTab === item.id ? 'active' : 'inactive']}
          onClick={() => onChange(item.id)}
        >
          {item.label}
        </button>
      ))}
    </div>
  )
}

function OverviewTab({
  user,
  eligibility,
  eligibilityUnavailable,
  onboarding,
  canReadRevenue,
  canReadProducts,
}: {
  user: AdminUserDetail
  eligibility: AdminEligibility | undefined
  eligibilityUnavailable: boolean
  onboarding: AdminOnboarding | undefined
  canReadRevenue: boolean
  canReadProducts: boolean
}) {
  const attentionItems = userAttentionItems(user, eligibility)
  const salesState = user.isSaleBlocked ? 'blocked' : (onboarding?.operations.sales ?? null)
  const payoutState = onboarding?.operations.payouts ?? null
  const salesValue = salesState === null ? 'Indisponível' : operationLabels.sales[salesState]
  const payoutValue = payoutState === null ? 'Indisponível' : operationLabels.payouts[payoutState]
  const salesHelperText =
    salesState === null
      ? 'Elegibilidade da Stripe indisponível.'
      : salesState === 'blocked'
        ? 'A conta não pode receber novas vendas.'
        : salesState === 'released'
          ? 'A conta pode operar normalmente.'
          : salesState === 'in_review'
            ? 'Liberadas após a aprovação do cadastro.'
            : 'Disponíveis após a aprovação do cadastro.'
  const payoutHelperText =
    payoutState === null
      ? 'Elegibilidade da Stripe indisponível.'
      : payoutState === 'blocked'
        ? 'Confira os requisitos e os dados bancários no Connect.'
        : payoutState === 'released'
          ? 'A conta está apta segundo o status atual.'
          : payoutState === 'in_review'
            ? 'Liberados após a aprovação do cadastro.'
            : 'Disponíveis após a aprovação do cadastro.'
  return (
    <div className={tabPanel} role="tabpanel">
      <AdminUserPerformance
        userId={user.id}
        canReadRevenue={canReadRevenue}
        canReadProducts={canReadProducts}
      />

      {attentionItems.length > 0 ? (
        <div className={attention}>
          <ShieldAlert size={20} />
          <div className={attentionCopy}>
            <strong>
              {attentionItems.length === 1
                ? '1 ponto precisa de atenção'
                : `${attentionItems.length} pontos precisam de atenção`}
            </strong>
            <span>{attentionItems.join(' · ')}</span>
          </div>
        </div>
      ) : null}

      <div className={summaryGrid}>
        <SummaryCard
          icon={<CircleDollarSign size={18} />}
          label="Vendas"
          value={salesValue}
          helperText={salesHelperText}
        />
        <SummaryCard
          icon={<WalletCards size={18} />}
          label="Recebimentos"
          value={payoutValue}
          helperText={payoutHelperText}
        />
        <SummaryCard
          icon={<MailCheck size={18} />}
          label="E-mail"
          value={user.emailVerified ? 'Verificado' : 'Não verificado'}
          helperText={
            user.emailVerified
              ? 'Identidade de e-mail confirmada.'
              : 'A confirmação do e-mail ainda está pendente.'
          }
        />
        <SummaryCard
          icon={<CalendarDays size={18} />}
          label="Conta criada em"
          value={formatDateTime(user.createdAt)}
          helperText="Data do primeiro cadastro na VendePay."
        />
      </div>

      <section className={section}>
        <div className={sectionHeader}>
          <div className={sectionHeading}>
            <h3 className={sectionTitle}>Acesso e operação</h3>
            <p className={sectionDescription}>
              Estados que determinam o que esta conta pode fazer agora.
            </p>
          </div>
        </div>
        <div className={definitionGrid}>
          <Definition
            label="Recebimento"
            value={
              eligibilityUnavailable || !eligibility
                ? 'Indisponível'
                : receivingPresentation(eligibility).label
            }
          />
          <Definition
            label="Status KYC"
            value={
              eligibilityUnavailable || !eligibility
                ? 'Indisponível'
                : eligibility.kycStatus
                  ? kycStatusLabels[eligibility.kycStatus]
                  : 'Sem observação'
            }
          />
          <Definition label="Vendas" value={salesValue} />
          <Definition label="Recebimentos" value={payoutValue} />
        </div>
      </section>
    </div>
  )
}

function ProfileTab({ user, userId }: { user: AdminUserDetail; userId: string }) {
  const profile = user.profile

  return (
    <div className={tabPanel} role="tabpanel">
      <AdminKycSupportPanel userId={userId} user={user} />
      <section className={section}>
        <SectionHeading
          title="Identificação"
          description="Dados declarados pelo titular durante o cadastro."
        />
        <div className={definitionGrid}>
          <Definition label="Nome completo" value={profileName(user)} />
          <Definition label="Tipo de pessoa" value={businessTypeLabel(profile?.businessType)} />
          <Definition label="Documento" value={profileDocument(user)} />
          <Definition label="Telefone" value={profile?.phone} />
          <Definition label="Nascimento" value={formatDate(profile?.birthDate)} />
          <Definition label="País" value={countryLabel(profile?.country)} />
          <Definition
            label="Pessoa politicamente exposta"
            value={pepLabel(profile?.politicalExposure)}
          />
        </div>
      </section>

      <section className={section}>
        <SectionHeading
          title="Atividade profissional"
          description="Informações comerciais usadas na análise cadastral."
        />
        <div className={definitionGrid}>
          <Definition label="Nome fantasia" value={profile?.businessName} />
          <Definition label="Razão social" value={profile?.legalName} />
          <Definition label="Faturamento mensal" value={monthlyRevenueLabel(user)} />
          <Definition label="MCC" value={profile?.businessProfile?.mcc} />
          <Definition
            label="Produto ou serviço"
            value={profile?.businessProfile?.productDescription}
          />
          <Definition label="Site" value={profile?.businessProfile?.url} />
        </div>
      </section>

      <section className={section}>
        <SectionHeading
          title="Endereço e histórico"
          description="Localização declarada e datas da última atualização do perfil."
        />
        <div className={definitionGrid}>
          <Definition label="Endereço" value={profileAddress(user)} full />
          <Definition label="Perfil criado em" value={optionalDateTime(profile?.createdAt)} />
          <Definition label="Última atualização" value={optionalDateTime(profile?.updatedAt)} />
        </div>
      </section>
    </div>
  )
}

function ProductsTab({
  user,
  canWriteProducts,
  onEditProduct,
}: {
  user: AdminUserDetail
  canWriteProducts: boolean
  onEditProduct: (product: ProductItem) => void
}) {
  const products = useAdminProducts(user.id)
  const items = products.data?.items ?? []
  return (
    <div className={tabPanel} role="tabpanel">
      <section className={section}>
        <div className={sectionHeader}>
          <SectionHeading
            title="Produtos deste usuário"
            description="Consulte o catálogo sem refazer a busca pelo produtor."
          />
          <span className={helper}>{items.length} produto(s)</span>
        </div>
        {products.isPending ? (
          <Skeleton height={220} />
        ) : products.isError ? (
          <ErrorState
            title="Não foi possível carregar os produtos"
            description="Tente novamente. Nenhum produto foi alterado."
            onRetry={() => void products.refetch()}
          />
        ) : items.length === 0 ? (
          <div className={emptyCopy}>Este usuário ainda não possui produtos cadastrados.</div>
        ) : (
          <div className={productList}>
            {items.map((product) => (
              <ProductRow
                key={product.id}
                product={product}
                canWriteProducts={canWriteProducts}
                onEditProduct={onEditProduct}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

function ProductRow({
  product,
  canWriteProducts,
  onEditProduct,
}: {
  product: ProductItem
  canWriteProducts: boolean
  onEditProduct: (product: ProductItem) => void
}) {
  return (
    <div className={productCard}>
      <div className={productIdentity}>
        <span className={strongValue}>{product.name}</span>
        <TechnicalId label="ID" value={product.id} />
        <div className={productMeta}>
          <span>{product.checkouts.length} checkout(s)</span>
          <span>{product.offers.length} oferta(s)</span>
          <span>{product.recurrencePlans.length} plano(s)</span>
        </div>
      </div>
      <MoneyValue amount={product.price.amount} currency={product.price.currency} />
      <div className={badgeRow}>
        <StatusBadge
          label={product.status === 'ACTIVE' ? 'Ativo' : product.status}
          tone={product.status === 'ACTIVE' ? 'success' : 'neutral'}
        />
        {canWriteProducts ? (
          <Button
            size="sm"
            variant="secondary"
            aria-label={`Solicitar alteração de ${product.name}`}
            onClick={() => onEditProduct(product)}
          >
            Solicitar alteração
          </Button>
        ) : null}
      </div>
    </div>
  )
}

function SummaryCard({
  icon,
  label,
  value,
  helperText,
}: {
  icon: ReactNode
  label: string
  value: string
  helperText: string
}) {
  return (
    <div className={summaryCard}>
      <span className={summaryIcon}>{icon}</span>
      <div className={summaryCopy}>
        <span className={eyebrow}>{label}</span>
        <span className={strongValue}>{value}</span>
        <span className={helper}>{helperText}</span>
      </div>
    </div>
  )
}

function SectionHeading({ title, description }: { title: string; description: string }) {
  return (
    <div className={sectionHeading}>
      <h3 className={sectionTitle}>{title}</h3>
      <p className={sectionDescription}>{description}</p>
    </div>
  )
}

function Definition({
  label,
  value,
  full = false,
}: {
  label: string
  value: ReactNode | null | undefined
  full?: boolean
}) {
  return (
    <div className={full ? fullDefinition : definition}>
      <span className={definitionLabel}>{label}</span>
      <span className={definitionValue}>{hasDisplayValue(value) ? value : 'Não informado'}</span>
    </div>
  )
}

function hasDisplayValue(value: ReactNode | null | undefined): boolean {
  return value !== null && value !== undefined && value !== ''
}

function userAttentionItems(
  user: AdminUserDetail,
  eligibility: AdminEligibility | undefined,
): string[] {
  const items: string[] = []
  if (eligibility && !eligibility.approved) items.push('recebimento bloqueado')
  if (user.isSaleBlocked) items.push('vendas bloqueadas')
  if (!user.emailVerified) items.push('e-mail não verificado')
  return items
}

function userInitials(user: AdminUserDetail): string {
  const name = profileName(user)
  const parts = name.split(/\s+/).filter(Boolean)
  if (parts.length === 0) return 'VP'
  return `${parts[0]?.[0] ?? ''}${parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : ''}`.toUpperCase()
}

function profileName(user: AdminUserDetail): string {
  const profile = user.profile
  return (
    [profile?.firstName, profile?.lastName].filter(Boolean).join(' ') ||
    profile?.legalName ||
    profile?.businessName ||
    user.name ||
    user.email
  )
}

function profileDocument(user: AdminUserDetail): string | undefined {
  if (!user.profile?.documentNumber) return undefined
  return [user.profile.documentType, user.profile.documentNumber].filter(Boolean).join(' · ')
}

function profileAddress(user: AdminUserDetail): string | undefined {
  const address = user.profile?.address
  if (!address) return undefined
  const lines = [
    [address.line1, address.line2].filter(Boolean).join(', '),
    [address.city, address.state].filter(Boolean).join(' · '),
    address.postalCode,
    countryLabel(address.country),
  ].filter(Boolean)
  return lines.join(': ') || undefined
}

function businessTypeLabel(value?: string): string | undefined {
  if (value === 'INDIVIDUAL') return 'Pessoa física'
  if (value === 'COMPANY') return 'Pessoa jurídica'
  return value
}

function countryLabel(value?: string): string | undefined {
  if (value === 'BR') return 'Brasil'
  if (value === 'US') return 'Estados Unidos'
  return value
}

function pepLabel(value?: string): string | undefined {
  if (value === 'none') return 'Não'
  if (value === 'existing') return 'Sim'
  return value
}

function monthlyRevenueLabel(user: AdminUserDetail): string | undefined {
  const monthlyRevenue = user.profile?.monthlyRevenue
  if (monthlyRevenue === undefined) return undefined
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: user.profile?.country === 'US' ? 'USD' : 'BRL',
  }).format(monthlyRevenue)
}

function formatDate(value?: string): string | undefined {
  if (!value) return undefined
  const parsed = new Date(`${value}T00:00:00`)
  if (Number.isNaN(parsed.getTime())) return value
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'long' }).format(parsed)
}

function optionalDateTime(value?: string): string | undefined {
  return value ? formatDateTime(value) : undefined
}

function formatDateTime(value: string): string {
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(parsed)
}
